import { Close } from "@mui/icons-material";
import {
  Box,
  Stack,
  Typography,
  Avatar as MuiAvatar,
  Dropdown,
  MenuItem,
  Menu,
  MenuButton,
  IconButton,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ApiUserInline, ApiUserProfile } from "griptonite-api-client";
import { orderBy } from "lodash";
import { useMemo } from "react";

import { useStaffProfileQuery } from "@/hooks/profile";
import { useAllVenueStaffData, useUpdateAllUserRolesMutation } from "@/hooks/venues";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import Avatar from "../Avatar";
import Sidebar from "../Sidebar";
import { StaffRole, useSelectedVenues } from "./Staff";

export const formatLocation = (basecamp: {
  city: string;
  country: string;
  state: string | null;
}) => {
  let cityPart = "";
  let emoji = "";
  if (basecamp.country == "US") {
    cityPart = `${basecamp.city}, ${basecamp.state}`;
  } else {
    cityPart = basecamp.city;
  }
  if (basecamp.country == "GB") {
    if (basecamp.state == "England") {
      emoji = "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
    } else if (basecamp.state == "Scotland") {
      emoji = "🏴󠁧󠁢󠁳󠁣󠁴󠁿";
    } else if (basecamp.state == "Wales") {
      emoji = "🏴󠁧󠁢󠁷󠁬󠁳󠁿";
    } else {
      emoji = "🇬🇧";
    }
  } else {
    emoji = basecamp.country
      .toUpperCase()
      .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397));
  }
  return `${emoji} ${cityPart}`;
};

export const StaffInfo = ({ profile }: { profile: ApiUserProfile | ApiUserInline }) => {
  return (
    <Stack alignItems="center" spacing={1}>
      <Avatar size={150} user={profile} />
      <Stack alignItems="center">
        <Typography level="title-lg">
          {profile.firstName} {profile.lastName}
        </Typography>
        {profile.basecamp ? (
          <Typography level="body-sm" textColor="neutral.600">
            {formatLocation(profile.basecamp)}
          </Typography>
        ) : null}
        <Typography level="body-sm" textColor="neutral.600">
          {profile.email}
        </Typography>
        {"creationTime" in profile ? (
          <Typography level="body-sm" textColor="neutral.600">
            Signed up {format(profile.creationTime, "do MMM yyyy")}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default function StaffSidebar({ userUuid }: { userUuid: string }) {
  const profileQuery = useStaffProfileQuery(userUuid);
  const { selectedVenues } = useSelectedVenues();
  const allVenuesData = useAllVenueStaffData(
    selectedVenues.length ? { venues: selectedVenues } : undefined,
  );
  const profile = profileQuery.data;

  const venuesByUuid = useMemo(
    () => Object.fromEntries(allVenuesData?.data?.venues.map((v) => [v.uuid, v]) || []),
    [allVenuesData?.data?.venues],
  );

  const venueRolesMap = allVenuesData?.data?.staff.find(
    (u) => u.user.uuid === userUuid,
  )?.venueRoles;

  const venueRoles = useMemo(() => {
    if (!venueRolesMap) {
      return [];
    }
    const roles = Array.from(venueRolesMap.entries(), ([venueUuid, role]) => ({
      ...role,
      venue: venuesByUuid[venueUuid],
    }));
    return orderBy(roles, "venue.name");
  }, [venueRolesMap, venuesByUuid]);

  const venuesWithoutRole = useMemo(
    () => allVenuesData?.data?.venues.filter((venue) => !venueRolesMap?.has(venue.uuid)),
    [allVenuesData?.data?.venues, venueRolesMap],
  );

  const navigate = useNavigate();

  return (
    <Sidebar
      sx={{
        padding: 2,
        overflowY: "auto",
        borderLeft: "1px solid",
        borderColor: "neutral.200",
        width: 320,
      }}
    >
      <IconButton
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
        }}
        onClick={() =>
          navigate({
            to: "..",
            from: "/gym/$venueUuid/staff/$userUuid",
            replace: true,
          })
        }
      >
        <Close />
      </IconButton>
      {profile ? (
        <Stack alignItems="center" spacing={1} sx={{ textAlign: "center" }}>
          <StaffInfo profile={profile} />
          <Stack
            sx={{
              borderRadius: 8,
              border: "1px solid",
              borderColor: "neutral.200",
              width: "100%",
              ".venue-role": {
                padding: 1,
                borderBottom: "1px solid",
                borderColor: "neutral.200",
                "&:last-child": {
                  borderBottom: "none",
                },
              },
            }}
          >
            {venueRoles.map((role) => {
              return (
                <Stack
                  alignItems="center"
                  spacing={1}
                  direction="row"
                  className="venue-role"
                  key={role.venue.uuid}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={0.5}>
                    <MuiAvatar
                      src={role.venue.logoUrl ? getThumbnailUrl(role.venue.logoUrl) : undefined}
                      alt={role.venue.name}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                      }}
                    />
                    <Typography level="body-md">{role.venue.name}</Typography>
                  </Stack>

                  <StaffRole role={role} userUuid={userUuid} venueUuid={role.venue.uuid} />
                </Stack>
              );
            })}
            {venuesWithoutRole?.map((venue) => {
              return (
                <Stack
                  alignItems="center"
                  spacing={1}
                  direction="row"
                  className="venue-role"
                  key={venue.uuid}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={0.5}>
                    <MuiAvatar
                      src={venue.logoUrl ? getThumbnailUrl(venue.logoUrl) : undefined}
                      alt={venue.name}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                      }}
                    />
                    <Typography level="body-md">{venue.name}</Typography>
                  </Stack>

                  <StaffRole role={undefined} userUuid={userUuid} venueUuid={venue.uuid} />
                </Stack>
              );
            })}
          </Stack>

          <Box sx={{ paddingTop: 1 }}>
            <SetRoleAtAllVenuesButton userUuid={userUuid} />
          </Box>
        </Stack>
      ) : null}
    </Sidebar>
  );
}

const SetRoleAtAllVenuesButton = ({ userUuid }: { userUuid: string }) => {
  const mutation = useUpdateAllUserRolesMutation();
  const { selectedVenues } = useSelectedVenues();
  const menuItems = [
    {
      label: "Make Admin",
      value: "ADMIN",
    },
    {
      label: "Make Staff",
      value: "STAFF",
    },
    {
      label: "Make Guest Setter",
      value: "ROUTE_SETTER",
    },
    {
      label: "Deactivate",
      value: "DEACTIVATE",
    },
  ] as const;
  return (
    <Dropdown>
      <MenuButton
        color="secondary"
        variant="solid"
        loading={mutation.isPending}
        disabled={mutation.isPending}
        loadingPosition="start"
      >
        Bulk Actions...
      </MenuButton>
      <Menu>
        {menuItems.map((item) => (
          <MenuItem
            key={item.value}
            color={item.value === "DEACTIVATE" ? "danger" : undefined}
            onClick={() => {
              mutation.mutate(
                {
                  userUuid,
                  ...(item.value !== "DEACTIVATE"
                    ? {
                        role: item.value,
                        isActive: true,
                        venues: selectedVenues.length ? selectedVenues : undefined,
                      }
                    : {
                        isActive: false,
                        venues: selectedVenues.length ? selectedVenues : undefined,
                      }),
                },
                {
                  onError: () =>
                    alert("An unexpected error occurred. Please try again or contact support."),
                },
              );
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Dropdown>
  );
};
